import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Stack, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../../utils/tableFunctions'
import EnhancedTableHead from '../../../components/table/EnhancedTableHead'
import TableButtonOpen from '../../../components/table/TableButtonOpen'
import { RouteConstants } from '../../../navigation/navigation-types'
import CustomTableCell from '../../../components/common/CustomTableCell'
import { SurveyResponseBriefDto } from '../../../services/api/sharedTypes/Email'
import { convertSurveyTypeToString } from '../../../utils/email-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'eventName',
        numeric: false,
        disablePadding: false,
        label: 'Událost',
    },
    {
        id: 'respondentName',
        numeric: false,
        disablePadding: false,
        label: 'Klient',
    },
    {
        id: 'eventStartsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum od',
        centered: true,
    },
    {
        id: 'eventEndsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum do',
        centered: true,
    },
    {
        id: 'address',
        numeric: false,
        disablePadding: false,
        label: 'Místo',
    },
    {
        id: 'surveyType',
        numeric: false,
        disablePadding: false,
        label: 'Dotazník',
    },
    {
        id: 'responseCreatedAt',
        numeric: false,
        disablePadding: false,
        label: 'Vytvořen',
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

interface FilledSurveysTableProps {
    surveys: SurveyResponseBriefDto[]
}

const FilledSurveysTable: React.FC<FilledSurveysTableProps> = ({ surveys }: FilledSurveysTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('responseCreatedAt')

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    return (
        <>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {surveys.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table size='small'>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={surveys.length}
                        />
                        <TableBody>
                            {stableSort(
                                surveys.map((survey) => {
                                    return {
                                        ...survey,
                                        address: `${survey.address.street}, ${survey.address.city}, ${survey.address.zip}`,
                                        eventEndsAt: new Date(survey.eventEndsAt).getTime(),
                                        eventStartsAt: new Date(survey.eventStartsAt).getTime(),
                                        eventIdName: `${survey.eventIdName.id ?? ''} - ${survey.eventIdName.name}`,
                                        eventName: `${survey.eventIdName.name}`,
                                        eventId: `${survey.eventIdName.id}`,
                                        responseCreatedAt: new Date(survey.responseCreatedAt).getTime(),
                                        respondentIdName: `${survey.respondentIdName.id ?? ''} - ${survey.respondentIdName.name}`,
                                        respondentName: `${survey.respondentIdName.name}`,
                                    }
                                }),
                                getComparator(order, orderBy)
                            ).map((row, index: number) => {
                                return (
                                    <TableRow hover tabIndex={-1} key={index}>
                                        <CustomTableCell
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.events + '/' + row.eventId}
                                            isText
                                        >
                                            {row.eventName}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)}
                                        >
                                            {row.respondentName}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)}
                                        >
                                            {new Date(row.eventStartsAt).toLocaleDateString('cs')}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)}
                                        >
                                            {new Date(row.eventEndsAt).toLocaleDateString('cs')}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)}
                                        >
                                            {row.address}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)}
                                        >
                                            {convertSurveyTypeToString(row.surveyType)}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)}
                                        >
                                            {new Date(row.responseCreatedAt).toLocaleDateString('cs')}
                                        </CustomTableCell>
                                        <CustomTableCell>
                                            <Stack spacing={theme.spacing(2)} direction='row' alignItems='center'>
                                                <TableButtonOpen
                                                    route={RouteConstants.emailsAndSurveys}
                                                    id={row.id}
                                                    hash={Number(row.surveyType)}
                                                />
                                            </Stack>
                                        </CustomTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    )
}

export default FilledSurveysTable
